/**
 * Subscription Calculator
 * Calculadora de precios de suscripción para gafas (3 Steps)
 */

class SubscriptionCalculator {
  constructor() {
    this.config = null;
    this.lensTypes = [];
    this.priceRules = []; // NEW: Vertical price rules
    this.lensIndices = [];
    this.replacementLensIndices = [];
    this.lensTreatments = [];
    this.accessories = [];
    
    // Contact Lenses Data
    this.contactLensTypes = [];
    this.contactLensDurations = [];
    this.contactLensPriceRules = [];
    this.contactLensProducts = []; // Array of contact lens product objects for Step 2
    
    this.products = []; // Array of product objects
    this.matrixSelections = []; // Step 1 selections
    
    this.currentStep = 1;
    this.isSubscription = true; // Default mode
    
    this.minProducts = 4;
    this.maxProducts = 6;
    this.emailJsInitialized = false;
    this.draggableInitialized = false;
    this.draggie = null; // Store Draggabilly instance
    this.positionFixedApplied = false; // Flag for CSS fix
    
    this.init();
  }

  init() {
    // 1. Load Data
    this.loadConfig();
    this.loadData();
    
    // 2. Initialize Steps
    this.initStep1Matrix();
    this.initStep2Simulator();
    this.initStep3Summary();
    
    // 3. Global Navigation
    this.initNavigation();
    
    // 4. Populate Store Selector
    this.populateStoreSelector();
    
    // 5. Show initial step
    this.showStep(1);
  }

  loadConfig() {
    const configElement = document.querySelector('[data-calculator-config]');
    if (configElement) {
      try {
        this.config = JSON.parse(configElement.textContent);
        this.minProducts = this.config.minProducts || 4;
        this.maxProducts = this.config.maxProducts || 6;
      } catch (error) {
        console.error('Error loading config:', error);
      }
    }
  }

  populateStoreSelector() {
    const select = document.querySelector('[data-store-select]');
    if (!select || !window.calculatorStores) return;

    window.calculatorStores.forEach(store => {
      const option = document.createElement('option');
      option.value = store.email || ''; 
      option.textContent = store.name;
      select.appendChild(option);
    });
  }

  loadData() {
    const loadJSON = (selector) => { 
      const el = document.querySelector(selector);
      return el ? JSON.parse(el.textContent) : [];
    };

    // Helper to convert Euros to Cents (since user enters 99 for 99€)
    const normalizePrices = (items) => {
      return items.map(item => {
        if (item.price !== undefined) {
          // If price is small (e.g. < 500), assume it's Euros and multiply by 100
          // This prevents double-converting if they fix it later in backend
          // Actually, user explicitly asked for this. Let's just multiply.
          // But wait, some prices might be 0.
          return { ...item, price: item.price * 100 };
        }
        return item;
      });
    };

    this.lensTypes = loadJSON('[data-lens-types]');
    this.priceRules = normalizePrices(loadJSON('[data-price-rules]'));
    this.lensIndices = normalizePrices(loadJSON('[data-lens-indices]'));
    this.replacementLensIndices = normalizePrices(loadJSON('[data-replacement-lens-indices]'));
    this.lensTreatments = loadJSON('[data-lens-treatments]');
    this.accessories = loadJSON('[data-accessories]');
    
    // Contact Lenses Data
    // Note: Contact lens prices come from Shopify products (already in cents), so NO normalization needed
    this.contactLensTypes = loadJSON('[data-contact-lens-types]');
    this.contactLensDurations = loadJSON('[data-contact-lens-durations]');
    this.contactLensPriceRules = loadJSON('[data-contact-lens-price-rules]');
    this.contactLensAccessories = loadJSON('[data-contact-lens-accessories]');
    
    // Product Images (from config)
    this.productImages = this.config.images || {};
    
  }

  isReplacementType(lensType) {
    if (!lensType) return false;
    // Primary: check if replacement indices are configured
    if (lensType.compatibleReplacementIndices && lensType.compatibleReplacementIndices.length > 0) {
      return true;
    }
    // Secondary: check category field
    const category = (lensType.category || '').toLowerCase();
    if (category.includes('reemplazo')) return true;
    // Tertiary: check name (most reliable - "Reemplazo Graduadas", etc.)
    const name = (lensType.name || '').toLowerCase();
    return name.includes('reemplazo');
  }

  getProductImage(typeHandle, isContactLens = false) {
    if (isContactLens) {
      return this.productImages.contactLenses || null;
    }
    
    // Priority 1: Image from lensType metaobject
    const lensType = this.lensTypes.find(t => t.handle === typeHandle);
    if (lensType && lensType.image) {
      return lensType.image;
    }
    
    // Priority 2: Fallback to settings images based on category
    if (lensType && lensType.category) {
      const category = lensType.category.toLowerCase();
      if (category.includes('sol')) {
        return this.productImages.sunGlasses || null;
      }
      if (category.includes('reemplazo')) {
        return this.productImages.replacementGlasses || null;
      }
    }
    
    // Priority 3: Default to graduated glasses
    return this.productImages.graduatedGlasses || null;
  }

  /* ==========================================================================
     STEP 1: VISUAL PLAN MATRIX
     ========================================================================== */
  
  initStep1Matrix() {
    // Global Index Selector
    const globalIndexSelect = document.querySelector('[data-global-index]');
    if (globalIndexSelect) {
      globalIndexSelect.addEventListener('change', (e) => {
      });
    }

    // Matrix Checkboxes
    // We delegate interaction to not require re-attaching if DOM changes
    const matrixTable = document.querySelector('.matrix-table');
    if (matrixTable) {
      matrixTable.addEventListener('change', (e) => {
        if (e.target.matches('.matrix-checkbox')) {
          this.handleMatrixChange(e);
        }
      });
    }

    // Add Row Buttons (Duplicate Row)
    const addButtons = document.querySelectorAll('.btn-matrix-add');
    addButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const row = e.target.closest('.matrix-row');
        if (row) {
          this.duplicateMatrixRow(row);
        }
      });
    });
  }

  handleMatrixChange(e) {
    // Optional: Real-time validation
  }

  duplicateMatrixRow(originalRow) {
    // Clone the row
    const newRow = originalRow.cloneNode(true);
    
    // Get all checkboxes from the original row
    const originalCheckboxes = originalRow.querySelectorAll('.matrix-checkbox');
    const newCheckboxes = newRow.querySelectorAll('.matrix-checkbox');
    
    // Copy checked state from original to new row
    originalCheckboxes.forEach((checkbox, index) => {
      if (newCheckboxes[index]) {
        newCheckboxes[index].checked = checkbox.checked;
      }
    });
    
    // Copy fabric/percentage value
    const originalFabric = originalRow.querySelector('[data-fabric]');
    const newFabric = newRow.querySelector('[data-fabric]');
    if (originalFabric && newFabric) {
      newFabric.value = originalFabric.value;
    }
    
    // Insert the new row after the original
    originalRow.parentNode.insertBefore(newRow, originalRow.nextSibling);
    
    // Re-attach event listeners to the new row
    const newAddButton = newRow.querySelector('.btn-matrix-add');
    if (newAddButton) {
      newAddButton.addEventListener('click', (e) => {
        e.preventDefault();
        const row = e.target.closest('.matrix-row');
        if (row) {
          this.duplicateMatrixRow(row);
        }
      });
    }
    
    // Attach change listeners to checkboxes in new row
    const matrixTable = document.querySelector('.matrix-table');
    if (matrixTable) {
      newCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
          this.handleMatrixChange(e);
        });
      });
    }
    
  }

  collectMatrixSelections() {
    const selections = [];
    const globalIndexHandle = document.querySelector('[data-global-index]').value;
    const globalIndex = this.lensIndices.find(i => i.handle === globalIndexHandle);

    const globalReplacementIndexHandle = document.querySelector('[data-global-replacement-index]')?.value;
    const globalReplacementIndex = this.replacementLensIndices.find(i => i.handle === globalReplacementIndexHandle);

    const rows = document.querySelectorAll('.matrix-row');
    
    rows.forEach(row => {
      const lensTypeHandle = row.dataset.lensType;
      const lensType = this.lensTypes.find(t => t.handle === lensTypeHandle);
      
      // Find checked treatments
      const checkedTreatments = Array.from(row.querySelectorAll('.matrix-checkbox:checked'))
        .map(cb => {
          const treatmentHandle = cb.dataset.treatment;
          return this.lensTreatments.find(t => t.handle === treatmentHandle);
        })
        .filter(Boolean);

      // Capture fabric value from the row (only when the Fabric column is enabled)
      const fabricSelect = this.config.step1ShowFabric ? row.querySelector('[data-fabric]') : null;
      const fabricValue = fabricSelect ? parseFloat(fabricSelect.value || 0) : 0;

      // Include if any treatment is checked (or if we add explicit selection logic later)
      // For now: Only rows with at least one treatment checked are added?
      // OR: We should check if the user wants to add the row even without treatments.
      // Let's assume: If any checkbox is checked, OR if we implement a "Row Selected" toggle.
      // Based on design, checkboxes are the main interaction.
      if (checkedTreatments.length > 0) {
        const isReplacement = this.isReplacementType(lensType);
        selections.push({
          type: lensType,
          index: isReplacement ? globalReplacementIndex : globalIndex,
          isReplacement: isReplacement,
          treatments: checkedTreatments,
          fabric: fabricValue
        });
      }
    });

    return selections;
  }

  collectContactLensSelections() {
    const selections = [];
    
    // Only process rows from the contact lenses table
    const clRows = document.querySelectorAll('.contact-lenses-table .matrix-row');
    
    clRows.forEach(row => {
      const lensTypeHandle = row.dataset.contactLens;
      const lensType = this.contactLensTypes.find(t => t.handle === lensTypeHandle);
      
      if (!lensType) return;
      
      // Find checked durations
      const checkedDurations = Array.from(row.querySelectorAll('.matrix-checkbox:checked'))
        .map(cb => {
          const durationHandle = cb.dataset.clDuration;
          const duration = this.contactLensDurations.find(d => d.handle === durationHandle);
          
          if (!duration) return null;
          
          // Find price rule for this combination
          const priceRule = this.contactLensPriceRules.find(
            rule => rule.lensType === lensTypeHandle && rule.duration === durationHandle
          );
          
          return {
            duration: duration,
            price: priceRule ? priceRule.price : 0,
            productHandle: priceRule ? priceRule.productHandle : null,
            productTitle: priceRule ? priceRule.productTitle : null
          };
        })
        .filter(Boolean);

      // Include if any duration is checked
      if (checkedDurations.length > 0) {
        selections.push({
          type: lensType,
          durations: checkedDurations
        });
      }
    });

    return selections;
  }

  /* ==========================================================================
     STEP 2: SIMULATOR
     ========================================================================== */

  initStep2Simulator() {
    // Add Product Button
    const addBtn = document.querySelector('[data-add-product]');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        const currentCount = document.querySelectorAll('.product-column').length;
        if (currentCount < this.maxProducts) {
          this.renderProductColumn(currentCount, null);
        }
      });
    }

    // Subscription Toggle (Segmented Control)
    const modeInputs = document.querySelectorAll('input[name="pricing-mode"]');
    modeInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        this.isSubscription = e.target.value === 'subscription';
        this.updatePricesUI();
      });
    });
  }

  generateSimulatorColumns(glassesSelections, contactLensSelections = []) {
    const grid = document.querySelector('[data-calculator-grid]');
    if (!grid) return;
    
    // Remove existing product columns (keep labels column)
    const productCols = grid.querySelectorAll('.product-column');
    productCols.forEach(col => col.remove());
    
    // Reset internal products array
    this.products = [];
    this.contactLensProducts = []; // Array for contact lens products
    
    // Store contact lens selections for later use (summary, email, etc.)
    this.contactLensSelections = contactLensSelections;
    

    let columnIndex = 0;

    // Create columns for each glasses selection
    glassesSelections.forEach((sel) => {
      this.renderProductColumn(columnIndex, sel);
      columnIndex++;
    });

    // Create columns for each contact lens selection
    contactLensSelections.forEach((sel) => {
      this.renderContactLensColumn(columnIndex, sel);
      columnIndex++;
    });

    // Fill up to minProducts with empty columns if needed
    const currentCount = columnIndex;
    if (currentCount < this.minProducts) {
      for (let i = currentCount; i < this.minProducts; i++) {
        this.renderProductColumn(i, null);
      }
    }
    
    this.updateGridClasses();
  }

  renderProductColumn(index, initialData = null) {
    // Extract fabric value from initialData if present
    const fabricValue = initialData?.fabric || 0;
    const grid = document.querySelector('[data-calculator-grid]');
    if (!grid) return;

    const productNumber = index + 1;
    const column = document.createElement('div');
    column.className = 'product-column';
    column.setAttribute('data-product-index', index);

    // Generate HTML
    column.innerHTML = this.getProductColumnHTML(index, productNumber, fabricValue);
    grid.appendChild(column);

    // Initialize Data Object
    this.products[index] = {
      type: initialData?.type?.handle || null,
      frame: null,
      index: initialData?.index || null,
      treatment1: initialData?.treatments[0] || null,
      treatment2: initialData?.treatments[1] || null,
      treatment3: initialData?.treatments[2] || null,
      personalization: Math.round(fabricValue * 100), // Convert fabric euros to cents
      accessory1: null,
      accessory2: null
    };

    // Populate Selectors
    this.populateTypeSelector(index);
    this.populateAccessoriesSelector(index);

    // Set Initial Values & Trigger Updates
    if (initialData) {
      // Set Type
      const typeSelect = column.querySelector(`[data-field="type"]`);
      if (typeSelect) {
        typeSelect.value = initialData.type.handle;
        // Trigger type change to populate dependencies
        this.handleTypeChange(index, initialData.type.handle, true); 
      }
      
      // Set Index (after population)
      if (initialData.index) {
        const indexSelect = column.querySelector(`[data-field="index"]`);
        if (indexSelect) {
          indexSelect.value = JSON.stringify(initialData.index);
          this.products[index].index = initialData.index;
        }
      }

      // Set Treatments (after population)
      initialData.treatments.forEach((t, i) => {
        const tSelect = column.querySelector(`[data-field="treatment${i+1}"]`);
        if (tSelect) {
          tSelect.value = JSON.stringify(t);
          this.products[index][`treatment${i+1}`] = t;
        }
      });
      
      // Update treatment selectors state after setting initial values
      this.updateTreatmentSelectorsState(index);
    } else {
        // Disable dependent fields for empty column
        this.populateFrameSelector(index, '');
        this.populateIndexSelector(index, '');
        this.populateTreatmentSelectors(index, '');
        // Hide warranty for empty column
        this.updateWarrantyVisibility(index, false);
    }

    // Add Listeners
    this.addProductEventListeners(index);
    
    // Remove button logic
    if (index >= this.minProducts) {
      const removeBtn = column.querySelector('[data-remove-product]');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => this.removeProduct(index));
      }
    }

    this.calculateProduct(index);
    this.updateGridClasses();
  }

  getProductColumnHTML(index, number, fabricValue = 0) {
    const texts = this.config.texts;
    return `
      <div class="grid-cell grid-header">
        <h3 class="product-column-title">${texts.textProduct || 'Producto'} ${number}</h3>
        ${index >= this.minProducts ? `
          <button class="btn-remove-product" data-remove-product="${index}" aria-label="${texts.labelDelete || 'Eliminar'}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>` : ''}
      </div>
      <div class="grid-cell grid-input"><select class="form-select" data-field="type" data-product="${index}"><option value="">${texts.placeholderSelectType || 'Seleccionar tipo'}</option></select></div>
      <div class="grid-cell grid-input"><select class="form-select" data-field="frame" data-product="${index}" disabled><option value="">${texts.placeholderSelectModel || 'Seleccionar modelo'}</option></select></div>
      <div class="grid-cell grid-input"><select class="form-select" data-field="index" data-product="${index}" disabled><option value="">${texts.placeholderSelectIndex || 'Seleccionar índice'}</option></select></div>
      <div class="grid-cell grid-input"><select class="form-select" data-field="treatment1" data-product="${index}" disabled><option value="">${texts.placeholderSelectTreatment || 'Seleccionar tratamiento'}</option></select></div>
      <div class="grid-cell grid-input treatment-hidden"><select class="form-select" data-field="treatment2" data-product="${index}" disabled><option value="">${texts.placeholderSelectTreatment || 'Seleccionar tratamiento'}</option></select></div>
      <div class="grid-cell grid-input treatment-hidden"><select class="form-select" data-field="treatment3" data-product="${index}" disabled><option value="">${texts.placeholderSelectTreatment || 'Seleccionar tratamiento'}</option></select></div>
      <div class="grid-cell grid-input">
        <input 
          type="number" 
          class="form-select" 
          data-field="personalization" 
          data-product="${index}"
          placeholder="€"
          min="0"
          step="0.01"
          value="${fabricValue}"
          disabled
        >
      </div>
      <div class="grid-cell grid-input"><select class="form-select" data-field="accessory1" data-product="${index}" disabled><option value="">${texts.placeholderNoAccessory || 'Sin complemento'}</option></select></div>
      <div class="grid-cell grid-input accessory-hidden"><select class="form-select" data-field="accessory2" data-product="${index}" disabled><option value="">${texts.placeholderNoAccessory || 'Sin complemento'}</option></select></div>
      <div class="grid-cell grid-value-static" data-row="warranty" data-warranty="${index}">
        <span class="warranty-value" style="display: none;"></span>
        <span class="warranty-placeholder">—</span>
      </div>
      <div class="grid-cell grid-total">
        <div class="product-total-value" data-total="${index}">0€</div>
      </div>
    `;
  }

  /* ==========================================================================
     CONTACT LENS COLUMN (Step 2)
     ========================================================================== */

  renderContactLensColumn(index, initialData = null) {
    const grid = document.querySelector('[data-calculator-grid]');
    if (!grid) return;

    const productNumber = index + 1;
    const column = document.createElement('div');
    column.className = 'product-column product-column--contact-lens';
    column.setAttribute('data-product-index', index);
    column.setAttribute('data-is-contact-lens', 'true');

    // Generate HTML for contact lens column
    column.innerHTML = this.getContactLensColumnHTML(index, productNumber);
    grid.appendChild(column);

    // Initialize Contact Lens Data Object
    const clIndex = this.contactLensProducts.length;
    this.contactLensProducts[clIndex] = {
      columnIndex: index,
      type: initialData?.type?.handle || null,
      model: null, // Will store the selected price rule
      personalization: 0,
      accessory1: null,
      accessory2: null
    };

    // Store reference to contact lens index in products array for unified handling
    this.products[index] = {
      isContactLens: true,
      contactLensIndex: clIndex
    };

    // Populate Contact Lens Type Selector
    this.populateContactLensTypeSelector(index, initialData?.type?.handle);
    this.populateAccessoriesSelector(index, true);

    // Set Initial Values if coming from Step 1
    if (initialData) {
      const typeSelect = column.querySelector(`[data-field="cl-type"]`);
      if (typeSelect && initialData.type) {
        typeSelect.value = initialData.type.handle;
        // Get preferred duration from Step 1 selection (first duration in the array)
        const preferredDuration = initialData.durations && initialData.durations.length > 0 
          ? initialData.durations[0].duration.handle 
          : null;
        // Trigger type change to populate model selector with preferred duration
        this.handleContactLensTypeChange(index, initialData.type.handle, preferredDuration);
      }
    }

    // Add Event Listeners
    this.addContactLensEventListeners(index);
    
    // Remove button logic
    if (index >= this.minProducts) {
      const removeBtn = column.querySelector('[data-remove-product]');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => this.removeProduct(index));
      }
    }

    this.calculateContactLensProduct(index);
    this.updateGridClasses();
  }

  getContactLensColumnHTML(index, number) {
    const texts = this.config.texts;
    return `
      <div class="grid-cell grid-header">
        <h3 class="product-column-title">${texts.textProduct || 'Producto'} ${number}</h3>
        ${index >= this.minProducts ? `
          <button class="btn-remove-product" data-remove-product="${index}" aria-label="${texts.labelDelete || 'Eliminar'}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>` : ''}
      </div>
      <div class="grid-cell grid-input">
        <select class="form-select" data-field="cl-type" data-product="${index}">
          <option value="">${texts.placeholderSelectType || 'Seleccionar tipo'}</option>
        </select>
      </div>
      <div class="grid-cell grid-input">
        <select class="form-select" data-field="cl-model" data-product="${index}" disabled>
          <option value="">${texts.placeholderSelectModel || 'Seleccionar modelo'}</option>
        </select>
      </div>
      <div class="grid-cell grid-input grid-cell--disabled"><span class="form-select-placeholder">—</span></div>
      <div class="grid-cell grid-input grid-cell--disabled"><span class="form-select-placeholder">—</span></div>
      <div class="grid-cell grid-input grid-cell--disabled"><span class="form-select-placeholder">—</span></div>
      <div class="grid-cell grid-input grid-cell--disabled"><span class="form-select-placeholder">—</span></div>
      <div class="grid-cell grid-input">
        <input type="number" class="form-select" data-field="cl-personalization" data-product="${index}" min="0" step="1" value="0" placeholder="0">
      </div>
      <div class="grid-cell grid-input"><select class="form-select" data-field="accessory1" data-product="${index}"><option value="">${texts.placeholderNoAccessory || 'Sin complemento'}</option></select></div>
      <div class="grid-cell grid-input accessory-hidden"><select class="form-select" data-field="accessory2" data-product="${index}" disabled><option value="">${texts.placeholderNoAccessory || 'Sin complemento'}</option></select></div>
      <div class="grid-cell grid-cell--disabled" data-row="warranty">
        <span class="form-select-placeholder">—</span>
      </div>
      <div class="grid-cell grid-total">
        <div class="product-total-value" data-total="${index}">0€</div>
      </div>
    `;
  }

  populateContactLensTypeSelector(index, preselectedType = null) {
    const select = document.querySelector(`[data-field="cl-type"][data-product="${index}"]`);
    if (!select) return;
    
    select.length = 1; // Keep placeholder
    
    this.contactLensTypes.forEach(type => {
      const opt = document.createElement('option');
      opt.value = type.handle;
      opt.textContent = type.name;
      if (preselectedType && type.handle === preselectedType) {
        opt.selected = true;
      }
      select.appendChild(opt);
    });
  }

  populateContactLensModelSelector(index, typeHandle, preferredDuration = null) {
    const select = document.querySelector(`[data-field="cl-model"][data-product="${index}"]`);
    if (!select) return;
    
    select.length = 1; // Keep placeholder
    select.disabled = !typeHandle;
    
    if (!typeHandle) return;
    
    
    // Filter price rules by lens type to get available models
    // Exclude models with price 0 or no price (products in Draft or not assigned)
    const modelsForType = this.contactLensPriceRules.filter(rule => rule.lensType === typeHandle && rule.price > 0);
    
    
    // Each metaobject entry is a unique model - use the metaobject name as key
    const uniqueModels = new Map();
    modelsForType.forEach(rule => {
      const key = rule.name || `${rule.lensType}-${rule.duration}-${rule.productHandle || Math.random()}`;
      if (!uniqueModels.has(key)) {
        uniqueModels.set(key, {
          productHandle: rule.productHandle,
          name: rule.name || rule.productTitle || rule.lensTypeName || 'Modelo',
          productTitle: rule.productTitle,
          price: rule.price,
          duration: rule.duration,
          durationName: rule.durationName,
          lensType: rule.lensType,
          variantId: rule.variantId,
          productId: rule.productId
        });
      }
    });
    
    
    // Add options - use name field for display
    let matchingIndex = -1;
    let optionIndex = 1; // Start at 1 (0 is placeholder)
    uniqueModels.forEach((model) => {
      const opt = document.createElement('option');
      opt.value = JSON.stringify(model);
      const title = model.name || 'Modelo sin nombre';
      opt.textContent = `${title} (${this.formatMoney(model.price)})`;
      select.appendChild(opt);
      
      // Check if this model matches the preferred duration
      if (preferredDuration && model.duration === preferredDuration && matchingIndex === -1) {
        matchingIndex = optionIndex;
      }
      optionIndex++;
    });
    
    // Preselect: prefer model matching duration, otherwise first model
    if (uniqueModels.size >= 1) {
      select.selectedIndex = matchingIndex > 0 ? matchingIndex : 1;
      this.handleContactLensModelChange(index, select.value);
    }
  }

  handleContactLensTypeChange(index, typeHandle, preferredDuration = null) {
    // Find the contact lens product data
    const productData = this.products[index];
    if (!productData || !productData.isContactLens) return;
    
    const clIndex = productData.contactLensIndex;
    this.contactLensProducts[clIndex].type = typeHandle;
    this.contactLensProducts[clIndex].model = null;
    
    // Populate model selector based on type, with optional preferred duration
    this.populateContactLensModelSelector(index, typeHandle, preferredDuration);
    
    // Enable/disable accessory fields based on type selection
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (col) {
      const accessory1Select = col.querySelector('[data-field="accessory1"]');
      const accessory2Select = col.querySelector('[data-field="accessory2"]');
      
      if (typeHandle) {
        if (accessory1Select) accessory1Select.disabled = false;
      } else {
        if (accessory1Select) {
          accessory1Select.disabled = true;
          accessory1Select.value = '';
        }
        if (accessory2Select) {
          accessory2Select.disabled = true;
          accessory2Select.value = '';
        }
      }
    }
    
    this.calculateContactLensProduct(index);
    this.updatePricesUI();
  }

  handleContactLensModelChange(index, modelValue) {
    const productData = this.products[index];
    if (!productData || !productData.isContactLens) return;
    
    const clIndex = productData.contactLensIndex;
    
    if (modelValue) {
      try {
        const model = JSON.parse(modelValue);
        this.contactLensProducts[clIndex].model = model;
      } catch {
        this.contactLensProducts[clIndex].model = null;
      }
    } else {
      this.contactLensProducts[clIndex].model = null;
    }
    
    this.calculateContactLensProduct(index);
    this.updatePricesUI();
  }

  addContactLensEventListeners(index) {
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (!col) return;

    const typeSelect = col.querySelector(`[data-field="cl-type"]`);
    const modelSelect = col.querySelector(`[data-field="cl-model"]`);
    const personalizationInput = col.querySelector(`[data-field="cl-personalization"]`);
    const accessory1Select = col.querySelector(`[data-field="accessory1"]`);
    const accessory2Select = col.querySelector(`[data-field="accessory2"]`);

    if (typeSelect) {
      typeSelect.addEventListener('change', (e) => this.handleContactLensTypeChange(index, e.target.value));
    }
    if (modelSelect) {
      modelSelect.addEventListener('change', (e) => this.handleContactLensModelChange(index, e.target.value));
    }
    if (personalizationInput) {
      personalizationInput.addEventListener('input', (e) => {
        this.handleContactLensPersonalizationChange(index, e.target.value);
      });
    }
    if (accessory1Select) {
      accessory1Select.addEventListener('change', (e) => {
        this.handleContactLensAccessoryChange(index, 1, e.target.value);
      });
    }
    if (accessory2Select) {
      accessory2Select.addEventListener('change', (e) => {
        this.handleContactLensAccessoryChange(index, 2, e.target.value);
      });
    }
  }

  handleContactLensPersonalizationChange(index, val) {
    const productData = this.products[index];
    if (!productData || !productData.isContactLens) return;
    
    const clIndex = productData.contactLensIndex;
    // Convert euros to cents (multiply by 100)
    const euros = parseFloat(val || 0);
    this.contactLensProducts[clIndex].personalization = Math.round(euros * 100);
    
    this.calculateContactLensProduct(index);
    this.updatePricesUI();
  }

  handleContactLensAccessoryChange(index, accessoryNum, value) {
    const productData = this.products[index];
    if (!productData || !productData.isContactLens) return;
    
    const clIndex = productData.contactLensIndex;
    
    if (value) {
      try {
        this.contactLensProducts[clIndex][`accessory${accessoryNum}`] = JSON.parse(value);
      } catch {
        this.contactLensProducts[clIndex][`accessory${accessoryNum}`] = null;
      }
    } else {
      this.contactLensProducts[clIndex][`accessory${accessoryNum}`] = null;
    }
    
    // Update accessory2 state
    if (accessoryNum === 1) {
      this.updateContactLensAccessoryState(index);
    }
    
    this.calculateContactLensProduct(index);
    this.updatePricesUI();
  }

  updateContactLensAccessoryState(index) {
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (!col) return;

    const accessory1 = col.querySelector('[data-field="accessory1"]');
    const accessory2 = col.querySelector('[data-field="accessory2"]');

    if (!accessory1 || !accessory2) return;

    const accessory2Cell = accessory2.closest('.grid-cell');

    if (accessory1.value) {
      accessory2.disabled = false;
      if (accessory2Cell) accessory2Cell.classList.remove('accessory-hidden');
    } else {
      accessory2.disabled = true;
      accessory2.value = '';
      const productData = this.products[index];
      if (productData && productData.isContactLens) {
        this.contactLensProducts[productData.contactLensIndex].accessory2 = null;
      }
      if (accessory2Cell) accessory2Cell.classList.add('accessory-hidden');
    }
  }

  calculateContactLensProduct(index) {
    const productData = this.products[index];
    if (!productData || !productData.isContactLens) return;
    
    const clIndex = productData.contactLensIndex;
    const clProduct = this.contactLensProducts[clIndex];
    
    let total = 0;
    
    // Model price
    if (clProduct.model) {
      total += clProduct.model.price || 0;
    }
    
    // Personalization (Otros)
    total += clProduct.personalization || 0;
    
    // Note: Contact lenses do NOT include warranty/insurance - only glasses do
    
    // Accessories
    if (clProduct.accessory1) total += clProduct.accessory1.price || 0;
    if (clProduct.accessory2) total += clProduct.accessory2.price || 0;
    
    // Update UI
    const totalEl = document.querySelector(`[data-total="${index}"]`);
    if (totalEl) {
      if (this.isSubscription) {
        const monthly = Math.round(total / (this.config.subscriptionMonths || 12));
        totalEl.textContent = `${this.formatMoney(monthly)}/mes`;
      } else {
        totalEl.textContent = this.formatMoney(total);
      }
    }
    
    return total;
  }

  /* ==========================================================================
     STEP 2: LOGIC & EVENTS
     ========================================================================== */

  populateTypeSelector(index) {
    const select = document.querySelector(`[data-field="type"][data-product="${index}"]`);
    if (!select) return;
    
    select.length = 1; 
    
    this.lensTypes.forEach(type => {
      const opt = document.createElement('option');
      opt.value = type.handle;
      opt.textContent = type.name;
      select.appendChild(opt);
    });
  }

  populateAccessoriesSelector(index, isContactLens = false) {
    // Determine which accessories list to use
    const accessoriesList = isContactLens ? this.contactLensAccessories : this.accessories;
    
    // Populate both accessory selectors
    for (let i = 1; i <= 2; i++) {
      const select = document.querySelector(`[data-field="accessory${i}"][data-product="${index}"]`);
      if (!select) continue;
      select.length = 1;
      
      accessoriesList.forEach(acc => {
        const opt = document.createElement('option');
        opt.value = JSON.stringify(acc);
        opt.textContent = `${acc.name} (${this.formatMoney(acc.price)})`;
        select.appendChild(opt);
      });
    }
    
    // Update accessory selectors visibility state
    this.updateAccessorySelectorsState(index);
  }
  
  updateAccessorySelectorsState(index) {
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (!col) return;

    const accessory1 = col.querySelector('[data-field="accessory1"]');
    const accessory2 = col.querySelector('[data-field="accessory2"]');

    if (!accessory1 || !accessory2) return;

    // Get parent grid-cell container for visibility control
    const accessory2Cell = accessory2.closest('.grid-cell');

    // Accessory 2 is enabled only if accessory 1 has a value
    if (accessory1.value) {
      accessory2.disabled = false;
      if (accessory2Cell) accessory2Cell.classList.remove('accessory-hidden');
    } else {
      accessory2.disabled = true;
      accessory2.value = '';
      this.products[index].accessory2 = null;
      if (accessory2Cell) accessory2Cell.classList.add('accessory-hidden');
    }
  }

  addProductEventListeners(index) {
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (!col) return;

    const addListener = (field, callback) => {
      const el = col.querySelector(`[data-field="${field}"]`);
      if (el) el.addEventListener('change', callback);
    };

    addListener('type', (e) => this.handleTypeChange(index, e.target.value));
    addListener('frame', (e) => this.handleFrameChange(index, e.target.value));
    addListener('index', (e) => this.handleIndexChange(index, e.target.value));
    addListener('treatment1', (e) => this.handleTreatmentChange(index, 1, e.target.value));
    addListener('treatment2', (e) => this.handleTreatmentChange(index, 2, e.target.value));
    addListener('treatment3', (e) => this.handleTreatmentChange(index, 3, e.target.value));
    // Use 'input' event for personalization to update in real-time (like contact lenses)
    const personalizationEl = col.querySelector(`[data-field="personalization"]`);
    if (personalizationEl) {
      personalizationEl.addEventListener('input', (e) => {
        this.handlePersonalizationChange(index, e.target.value);
      });
    }
    addListener('accessory1', (e) => this.handleAccessoryChange(index, 1, e.target.value));
    addListener('accessory2', (e) => this.handleAccessoryChange(index, 2, e.target.value));
  }

  handleTypeChange(index, typeHandle, skipClear = false) {
    this.products[index].type = typeHandle;
    
    if (!skipClear) {
      this.products[index].frame = null;
      this.products[index].index = null;
      this.products[index].treatment1 = null;
      this.products[index].treatment2 = null;
      this.products[index].treatment3 = null;
    }

    this.populateFrameSelector(index, typeHandle);
    this.populateIndexSelector(index, typeHandle);
    this.populateTreatmentSelectors(index, typeHandle);
    
    // Enable/disable personalization and accessory fields based on type selection
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (col) {
      const personalizationInput = col.querySelector('[data-field="personalization"]');
      const accessory1Select = col.querySelector('[data-field="accessory1"]');
      const accessory2Select = col.querySelector('[data-field="accessory2"]');
      
      if (typeHandle) {
        // Type selected - enable fields
        if (personalizationInput) personalizationInput.disabled = false;
        if (accessory1Select) accessory1Select.disabled = false;
        // accessory2 state is managed by updateAccessorySelectorsState
      } else {
        // No type selected - disable fields
        if (personalizationInput) {
          personalizationInput.disabled = true;
          personalizationInput.value = '0';
          this.products[index].personalization = 0;
        }
        if (accessory1Select) {
          accessory1Select.disabled = true;
          accessory1Select.value = '';
          this.products[index].accessory1 = null;
        }
        if (accessory2Select) {
          accessory2Select.disabled = true;
          accessory2Select.value = '';
          this.products[index].accessory2 = null;
        }
      }
      
      // Update accessory2 visibility
      this.updateAccessorySelectorsState(index);
      
      // Update warranty visibility - show only if type is selected
      this.updateWarrantyVisibility(index, !!typeHandle);
    }
    
    this.calculateProduct(index);
  }
  
  updateWarrantyVisibility(index, hasProduct) {
    const warrantyCell = document.querySelector(`[data-warranty="${index}"]`);
    if (!warrantyCell) return;

    const valueEl = warrantyCell.querySelector('.warranty-value');
    const placeholderEl = warrantyCell.querySelector('.warranty-placeholder');
    const p = this.products[index];
    const show = !!hasProduct && this.isSubscription;

    if (show) {
      // Set the dynamic value: +warranty (benefits) or -discount (discount mode)
      if (valueEl && p) {
        valueEl.textContent = this.formatMoney(this.getAdditionalAmount(this.getProductSubtotal(p)));
      }
      if (valueEl) valueEl.style.display = '';
      if (placeholderEl) placeholderEl.style.display = 'none';
    } else {
      if (valueEl) valueEl.style.display = 'none';
      if (placeholderEl) placeholderEl.style.display = '';
    }
  }

  populateFrameSelector(index, typeHandle) {
    const select = document.querySelector(`[data-field="frame"][data-product="${index}"]`);
    if (!select) return;
    select.length = 1;
    select.disabled = !typeHandle;

    if (!typeHandle) return;

    // Filter rules for this lens type to find available tiers
    const validRules = this.priceRules.filter(r => r.lensType === typeHandle);
    const tiers = [...new Set(validRules.map(r => r.tier))];
    
    let studioOptionValue = null;
    let firstOptionValue = null;

    tiers.forEach((tier, idx) => {
      const rule = validRules.find(r => r.tier === tier);
      const opt = document.createElement('option');
      const val = JSON.stringify({ tier: tier, price: rule.price });
      opt.value = val;
      opt.textContent = `${tier} (${this.formatMoney(rule.price)})`;
      select.appendChild(opt);

      // Store first option as fallback
      if (idx === 0) {
        firstOptionValue = val;
      }

      // Check for default Studio model
      if (tier.toLowerCase().includes('studio')) {
        studioOptionValue = val;
      }
    });

    // Auto-select: prefer Studio, fallback to first option
    const autoSelectValue = studioOptionValue || firstOptionValue;
    if (autoSelectValue) {
      select.value = autoSelectValue;
      this.handleFrameChange(index, autoSelectValue);
    }
  }

  populateIndexSelector(index, typeHandle) {
    const select = document.querySelector(`[data-field="index"][data-product="${index}"]`);
    if (!select) return;
    select.length = 1;
    
    const lensType = this.lensTypes.find(t => t.handle === typeHandle);
    if (!lensType) {
        select.disabled = true;
        return;
    }

    const isReplacement = this.isReplacementType(lensType);
    const indicesSource = isReplacement ? this.replacementLensIndices : this.lensIndices;
    let validIndices;

    if (isReplacement) {
      const compatibleHandles = lensType.compatibleReplacementIndices || [];
      // If compatible replacement handles are defined, filter by them;
      // otherwise show all replacement indices as fallback
      validIndices = compatibleHandles.length > 0
        ? indicesSource.filter(i => compatibleHandles.includes(i.handle))
        : [...indicesSource];
    } else {
      const compatibleHandles = lensType.compatibleIndices || [];
      validIndices = indicesSource.filter(i => compatibleHandles.includes(i.handle));
    }

    select.disabled = validIndices.length === 0;

    validIndices.forEach(ind => {
      const opt = document.createElement('option');
      opt.value = JSON.stringify(ind);
      opt.textContent = `${ind.value} ${ind.price > 0 ? '(+' + this.formatMoney(ind.price) + ')' : ''}`;
      select.appendChild(opt);
    });
  }

  populateTreatmentSelectors(index, typeHandle) {
    const lensType = this.lensTypes.find(t => t.handle === typeHandle);
    if (!lensType) return;

    const compatibleTreatments = this.lensTreatments.filter(t => 
      lensType.compatibleTreatments.includes(t.handle)
    );

    for (let i = 1; i <= 3; i++) {
      const select = document.querySelector(`[data-field="treatment${i}"][data-product="${index}"]`);
      if (!select) continue;
      select.length = 1;
      
      const cell = select.closest('.grid-cell');
      
      // Treatment 1 is enabled if type is selected
      // Treatment 2 and 3 start disabled and hidden
      if (i === 1) {
        select.disabled = !typeHandle;
      } else {
        select.disabled = true;
        // Hide treatment 2 and 3 initially
        if (cell) cell.classList.add('treatment-hidden');
      }

      compatibleTreatments.forEach(treatment => {
        const opt = document.createElement('option');
        opt.value = JSON.stringify(treatment);
        // Show price x2 (one treatment per eye)
        opt.textContent = `${treatment.name} (${this.formatMoney(treatment.price * 2)})`;
        select.appendChild(opt);
      });
    }
    
    // Update state after populating
    this.updateTreatmentSelectorsState(index);
  }

  handleFrameChange(index, val) {
    this.products[index].frame = val ? JSON.parse(val) : null;
    this.calculateProduct(index);
  }
  
  handleIndexChange(index, val) {
    this.products[index].index = val ? JSON.parse(val) : null;
    this.calculateProduct(index);
  }

  handleTreatmentChange(index, num, val) {
    this.products[index][`treatment${num}`] = val ? JSON.parse(val) : null;
    
    // Enable/disable next treatment selector based on current selection
    this.updateTreatmentSelectorsState(index);
    
    this.calculateProduct(index);
  }

  updateTreatmentSelectorsState(index) {
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (!col) return;

    const treatment1 = col.querySelector('[data-field="treatment1"]');
    const treatment2 = col.querySelector('[data-field="treatment2"]');
    const treatment3 = col.querySelector('[data-field="treatment3"]');

    if (!treatment1 || !treatment2 || !treatment3) return;

    // Get parent grid-cell containers for visibility control
    const treatment2Cell = treatment2.closest('.grid-cell');
    const treatment3Cell = treatment3.closest('.grid-cell');

    // Treatment 2 is enabled only if treatment 1 has a value
    if (treatment1.value) {
      treatment2.disabled = false;
      if (treatment2Cell) treatment2Cell.classList.remove('treatment-hidden');
    } else {
      treatment2.disabled = true;
      treatment2.value = '';
      this.products[index].treatment2 = null;
      if (treatment2Cell) treatment2Cell.classList.add('treatment-hidden');
    }

    // Treatment 3 is enabled only if treatment 2 has a value
    if (treatment2.value) {
      treatment3.disabled = false;
      if (treatment3Cell) treatment3Cell.classList.remove('treatment-hidden');
    } else {
      treatment3.disabled = true;
      treatment3.value = '';
      this.products[index].treatment3 = null;
      if (treatment3Cell) treatment3Cell.classList.add('treatment-hidden');
    }
  }

  handlePersonalizationChange(index, val) {
    // Convert euros to cents (multiply by 100)
    const euros = parseFloat(val || 0);
    this.products[index].personalization = Math.round(euros * 100);
    this.calculateProduct(index);
  }

  handleAccessoryChange(index, num, val) {
    this.products[index][`accessory${num}`] = val ? JSON.parse(val) : null;
    
    // Update accessory selectors visibility state
    this.updateAccessorySelectorsState(index);
    
    this.calculateProduct(index);
  }

  removeProduct(index) {
    const col = document.querySelector(`.product-column[data-product-index="${index}"]`);
    if (!col) return;
    
    // Check if this is a contact lens column
    const productData = this.products[index];
    if (productData && productData.isContactLens) {
      // Remove from contactLensProducts array
      const clIndex = productData.contactLensIndex;
      if (clIndex !== undefined) {
        delete this.contactLensProducts[clIndex];
        this.contactLensProducts = this.contactLensProducts.filter(p => p !== undefined);
      }
    }
    
    col.remove();
    delete this.products[index];
    this.products = this.products.filter(p => p !== undefined); // Re-index
    this.updatePricesUI();
    this.updateGridClasses();
  }

  updateGridClasses() {
    // Update Calculator Grid
    const grid = document.querySelector('[data-calculator-grid]');
    if (grid) {
      const count = grid.querySelectorAll('.product-column').length;
      grid.className = grid.className.replace(/has-\d+-products/g, '').trim();
      if (count >= 4) {
        grid.classList.add(`has-${count}-products`);
      }
    }

    // Update Summary Grid
    const summaryGrid = document.querySelector('[data-summary-grid]');
    if (summaryGrid) {
      const count = summaryGrid.children.length;
      summaryGrid.className = summaryGrid.className.replace(/has-\d+-products/g, '').trim();
      if (count >= 4) {
        summaryGrid.classList.add(`has-${count}-products`);
      }
    }
  }

  /* ==========================================================================
     CALCULATIONS & UI UPDATES
     ========================================================================== */

  /* --- Additional row helpers (Benefits legacy vs Discount) --- */

  // Sum of all line items of a glasses product (PVP), WITHOUT the additional row.
  getProductSubtotal(p) {
    if (!p) return 0;
    let s = 0;
    if (p.frame) s += p.frame.price;
    if (p.index) s += p.index.price;
    if (p.treatment1) s += p.treatment1.price * 2;
    if (p.treatment2) s += p.treatment2.price * 2;
    if (p.treatment3) s += p.treatment3.price * 2;
    s += p.personalization || 0;
    if (p.accessory1) s += p.accessory1.price;
    if (p.accessory2) s += p.accessory2.price;
    return s;
  }

  // Signed amount of the additional subscription row for a given subtotal.
  // Benefits mode -> +warranty; Discount mode -> -(subtotal * pct/100).
  getAdditionalAmount(subtotal) {
    if (this.config.additionalRowMode === 'benefits') {
      return this.config.warrantyPrice || 0;
    }
    const pct = this.config.discountPercentage || 0;
    return -Math.round(subtotal * pct / 100);
  }

  // Label shown for the additional detail row.
  getAdditionalLabel() {
    if (this.config.additionalRowMode === 'benefits') {
      return this.config.texts.labelBenefits || 'Beneficios y Seguros';
    }
    return this.config.texts.discountLabel || 'Descuento';
  }

  // Total of a glasses product. The additional row only applies in subscription
  // mode and when a product type is selected.
  getGlassesTotal(p, isSubscription = this.isSubscription) {
    const subtotal = this.getProductSubtotal(p);
    if (isSubscription && p && p.type) {
      return subtotal + this.getAdditionalAmount(subtotal);
    }
    return subtotal;
  }

  calculateProduct(index) {
    const p = this.products[index];
    if (!p) return;

    const total = this.getGlassesTotal(p);

    // Update cell
    const cell = document.querySelector(`[data-total="${index}"]`);
    if (cell) cell.textContent = this.formatMoney(total);

    this.updatePricesUI();
  }

  updatePricesUI() {
    let grandTotal = 0;
    let grandMonthlyRounded = 0; // Sum of individually rounded monthly values
    
    // 1. Update warranty display based on subscription mode
    // Instead of hiding rows, show "—" in purchase mode to maintain layout
    this.products.forEach((p, index) => {
      if (p && !p.isContactLens) {
        const hasProduct = !!p.type;
        this.updateWarrantyVisibility(index, hasProduct && this.isSubscription);
      }
    });

    // HTML accumulator for static list
    let productsListHTML = '';
    let productCounter = 0;

    // 2. Loop products & calculate totals (glasses)
    this.products.forEach((p, index) => {
      // Skip contact lens products (handled separately)
      if (p && p.isContactLens) return;
      
      if (p && p.type) {
        const pTotal = this.getGlassesTotal(p);
        grandTotal += pTotal;

        // Calculate rounded monthly and accumulate
        const pMonthly = pTotal / this.config.subscriptionMonths;
        const pMonthlyRounded = Math.round(pMonthly);
        grandMonthlyRounded += pMonthlyRounded;

        // Update individual product total in Grid (Step 2)
        const cell = document.querySelector(`[data-total="${index}"]`);
        if (cell) cell.textContent = this.formatMoney(pTotal);

        productCounter++;
        // Build HTML for list
        productsListHTML += `
            <li>
                <span>${this.config.texts.textProduct || 'Producto'} ${productCounter}</span>
                <span>${this.formatMoney(pMonthlyRounded)} / mes</span>
            </li>
        `;
      }
    });

    // 2.1 Loop contact lens products & calculate totals
    // Note: Contact lenses do NOT include warranty/insurance - only glasses do
    if (this.contactLensProducts && this.contactLensProducts.length > 0) {
      this.contactLensProducts.forEach((clProduct) => {
        if (clProduct && clProduct.model) {
          let pTotal = 0;
          pTotal += clProduct.model.price || 0;
          pTotal += clProduct.personalization || 0;
          if (clProduct.accessory1) pTotal += clProduct.accessory1.price || 0;
          if (clProduct.accessory2) pTotal += clProduct.accessory2.price || 0;
          
          grandTotal += pTotal;

          // Calculate rounded monthly and accumulate
          const pMonthly = pTotal / this.config.subscriptionMonths;
          const pMonthlyRounded = Math.round(pMonthly);
          grandMonthlyRounded += pMonthlyRounded;

          // Update individual product total in Grid (Step 2)
          const cell = document.querySelector(`[data-total="${clProduct.columnIndex}"]`);
          if (cell) cell.textContent = this.formatMoney(pTotal);

          productCounter++;
          // Build HTML for list
          productsListHTML += `
              <li>
                  <span>${this.config.texts.textProduct || 'Producto'} ${productCounter}</span>
                  <span>${this.formatMoney(pMonthlyRounded)} / mes</span>
              </li>
          `;
        }
      });
    }
    
    // 2.1 Update STATIC Card (Step 2 Background)
    // This ensures that if the floating card moves or glitches, the background card is correct.
    const staticList = document.querySelector('[data-summary-product-list]');
    const staticTotal = document.querySelector('[data-total-monthly]');
    
    if (staticList) staticList.innerHTML = productsListHTML;
    
    // Use sum of rounded monthly values for visual consistency
    if (staticTotal) staticTotal.textContent = this.formatMoney(grandMonthlyRounded);

    // 3. Update Floating Summary Card
    const floatCard = document.querySelector('[data-floating-card]');
    if (floatCard) {
        let html = '';
        const isStep3 = this.currentStep === 3;

        // Step 1: Only floating button (no header)
        if (this.currentStep === 1) {
            html = `
                <div class="calculator-summary-card step1-floating-button">
                  <button class="btn-next-step" data-action="next-step-2">${this.config.texts.buttonNext || 'Siguiente'}</button>
                </div>
            `;
        } else if (this.isSubscription) {
            if (isStep3) {
                 const step3Label = this.config.additionalRowMode === 'benefits'
                   ? (this.config.texts.labelSubscriptionTotal || 'Total garantía incl.')
                   : (this.config.texts.discountTotalLabel || 'Total');
                 html = `
                    <div class="calculator-summary-card">
                      <div class="summary-card-header" style="border-radius: var(--calc-summary-card-radius, 12px);">
                        <div class="summary-header-label">${step3Label}</div>
                        <div class="summary-header-price-wrapper">
                          <span class="summary-header-price">${this.formatMoney(grandMonthlyRounded)}</span>
                          <span class="summary-header-period">${this.config.texts.textPerMonth || '/mes'}</span>
                        </div>
                      </div>
                    </div>
                `;
            } else {
                // Subscription Mode HTML (Step 2)
                html = `
                    <div class="calculator-summary-card">
                      <div class="summary-card-header">
                        <div class="summary-header-label">${this.config.texts.labelSubscriptionFee || 'Cuota suscripción'}</div>
                        <div class="summary-header-price-wrapper">
                          <span class="summary-header-price">${this.formatMoney(grandMonthlyRounded)}</span>
                          <span class="summary-header-period">/mes</span>
                        </div>
                      </div>
                      
                      <div class="summary-card-body">
                        <ul class="summary-product-list">
                            ${productsListHTML}
                        </ul>
                      </div>

                      <div class="summary-card-footer">
                        <button class="btn-next-step" data-action="next-step-3">${this.config.texts.buttonNext || 'Siguiente'}</button>
                      </div>
                    </div>
                `;
            }
        } else {
            // Purchase Mode - Calculate subscription comparison using rounded values
            let monthlySubRounded = 0;
            this.products.forEach(p => {
              if (p && !p.isContactLens && p.type) {
                // Subscription total for the "Suscríbete" comparison (force subscription)
                const pTotal = this.getGlassesTotal(p, true);
                monthlySubRounded += Math.round(pTotal / this.config.subscriptionMonths);
              }
            });
            // Add contact lens products
            if (this.contactLensProducts && this.contactLensProducts.length > 0) {
              this.contactLensProducts.forEach(clProduct => {
                if (clProduct && clProduct.model) {
                  let pTotal = clProduct.model.price || 0;
                  pTotal += clProduct.personalization || 0;
                  if (clProduct.accessory1) pTotal += clProduct.accessory1.price || 0;
                  if (clProduct.accessory2) pTotal += clProduct.accessory2.price || 0;
                  monthlySubRounded += Math.round(pTotal / this.config.subscriptionMonths);
                }
              });
            }

            html = `
                <div class="calculator-summary-card">
                  <div class="summary-card-header">
                    <div class="summary-header-label">${this.config.texts.labelTotal || 'Total'}</div>
                    <div class="summary-header-price-wrapper">
                      <span class="summary-header-price">${this.formatMoney(grandTotal)}</span>
                    </div>
                  </div>
                  
                  <div class="summary-card-body purchase-mode" ${isStep3 ? 'style="border-radius: 0 0 12px 12px;"' : ''}>
                    <div class="purchase-comparison-label">${this.config.texts.textSubscribeCallout || 'Suscríbete'}</div>
                    <div class="purchase-comparison-price">
                        ${this.formatMoney(monthlySubRounded)} <span class="purchase-comparison-unit">${this.config.texts.textPerMonth || '/mes'}</span>
                    </div>
                  </div>

                  ${!isStep3 ? `
                  <div class="summary-card-footer">
                    <button class="btn-next-step" data-action="next-step-3">${this.config.texts.buttonNext || 'Siguiente'}</button>
                  </div>` : ''}
                </div>
            `;
        }
        floatCard.innerHTML = html;
        
        // Re-attach event listeners
        const btnNextStep2 = floatCard.querySelector('[data-action="next-step-2"]');
        if (btnNextStep2) {
            btnNextStep2.addEventListener('click', () => {
                const glassesSelections = this.collectMatrixSelections();
                const contactLensSelections = this.collectContactLensSelections();
                this.generateSimulatorColumns(glassesSelections, contactLensSelections);
                this.showStep(2);
            });
        }
        
        const btnNextStep3 = floatCard.querySelector('[data-action="next-step-3"]');
        if (btnNextStep3) {
            btnNextStep3.addEventListener('click', () => {
                this.renderSummary();
                this.showStep(3);
            });
        }
    }
  }

  /* ==========================================================================
     STEP 3: SUMMARY
     ========================================================================== */

  initStep3Summary() {
    // Budget email button is now handled in initBudgetModal()
    this.initBudgetModal();
    // Info modal: Benefits & Insurance
    this.initModal('[data-open-benefits-modal]', '[data-benefits-modal]', '[data-action="close-benefits-modal"]');
    // Info modal: "Lo que necesitas saber"
    this.initModal('[data-open-know-modal]', '[data-know-modal]', '[data-action="close-know-modal"]');
    this.initIframeEmailCapture();
  }

  // Generic info-modal wiring: moves the overlay to <body> (to escape stacking
  // contexts), copies the calculator's CSS variables, and binds open/close.
  initModal(openSelector, overlaySelector, closeSelector) {
    const openBtns = document.querySelectorAll(openSelector);
    const closeBtn = document.querySelector(closeSelector);
    const modalOverlay = document.querySelector(overlaySelector);
    if (!modalOverlay) return;

    const calculator = document.querySelector('.subscription-calculator');
    if (modalOverlay.parentElement !== document.body) {
      if (calculator) {
        const styles = calculator.getAttribute('style');
        if (styles) {
          modalOverlay.setAttribute('style', styles);
          modalOverlay.style.display = 'none';
        }
      }
      document.body.appendChild(modalOverlay);
    }

    openBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modalOverlay.style.setProperty('display', 'flex', 'important');
        document.body.style.overflow = 'hidden';
      });
    });

    const closeModal = () => {
      modalOverlay.style.display = 'none';
      document.body.style.overflow = '';
    };

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) closeModal();
    });
  }

  initBudgetModal() {
    const openBtn = document.querySelector('[data-action="open-budget-modal"]');
    const closeBtn = document.querySelector('[data-action="close-budget-modal"]');
    const modalOverlay = document.querySelector('[data-budget-modal]');
    const calculator = document.querySelector('.subscription-calculator');

    // Move modal to body to avoid stacking context issues
    if (modalOverlay && modalOverlay.parentElement !== document.body) {
      if (calculator) {
        // Copy CSS variables from calculator to modal to preserve theming
        const styles = calculator.getAttribute('style');
        if (styles) {
            modalOverlay.setAttribute('style', styles);
            modalOverlay.style.display = 'none'; // Ensure it stays hidden initially
        }
      }
      document.body.appendChild(modalOverlay);
    }

    if (openBtn && modalOverlay) {
      openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // Always reset to step 1 when opening
        this.resetModalSteps(modalOverlay);
        
        // Show correct iframe based on mode (subscription or purchase)
        const iframeSubscription = modalOverlay.querySelector('[data-iframe-subscription]');
        const iframePurchase = modalOverlay.querySelector('[data-iframe-purchase]');
        if (iframeSubscription && iframePurchase) {
          if (this.isSubscription) {
            iframeSubscription.style.display = 'block';
            iframePurchase.style.display = 'none';
          } else {
            iframeSubscription.style.display = 'none';
            iframePurchase.style.display = 'block';
          }
        }
        
        modalOverlay.style.setProperty('display', 'flex', 'important');
        document.body.style.overflow = 'hidden';
      });
    }

    const closeModal = () => {
      if (modalOverlay) {
        modalOverlay.style.display = 'none';
        document.body.style.overflow = '';
        this.resetModalSteps(modalOverlay);
      }
    };

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    if (modalOverlay) {
      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          closeModal();
        }
      });
    }
    
    // Modal step navigation
    const nextStepBtn = document.querySelector('[data-action="modal-next-step"]');
    if (nextStepBtn) {
      nextStepBtn.addEventListener('click', () => {
        const step1 = modalOverlay.querySelector('[data-modal-step="1"]');
        const step2 = modalOverlay.querySelector('[data-modal-step="2"]');
        if (step1 && step2) {
          step1.style.display = 'none';
          step2.style.display = 'block';
        }
      });
    }
    
    // Send budget email button
    const sendBudgetBtn = document.querySelector('[data-action="send-budget-email"]');
    if (sendBudgetBtn) {
      sendBudgetBtn.addEventListener('click', () => {
        this.sendBudgetEmail();
      });
    }
  }
  
  resetModalSteps(modalOverlay) {
    if (!modalOverlay) {
      modalOverlay = document.querySelector('[data-budget-modal]');
    }
    if (!modalOverlay) return;
    
    const step1 = modalOverlay.querySelector('[data-modal-step="1"]');
    const step2 = modalOverlay.querySelector('[data-modal-step="2"]');
    const feedback = modalOverlay.querySelector('[data-budget-feedback]');
    const emailInput = modalOverlay.querySelector('[data-email-input]');
    
    if (step1) {
      step1.removeAttribute('style');
      step1.style.display = 'block';
    }
    if (step2) {
      step2.style.display = 'none';
    }
    if (feedback) {
      feedback.textContent = '';
      feedback.className = 'budget-feedback';
    }
    if (emailInput) emailInput.value = '';
  }

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
  
  sendBudgetEmail() {
    const emailInput = document.querySelector('[data-email-input]');
    const storeSelect = document.querySelector('[data-store-select]');
    const feedbackEl = document.querySelector('[data-budget-feedback]');
    const texts = this.config.texts;
    
    const email = emailInput ? emailInput.value.trim() : '';
    const storeEmail = storeSelect ? storeSelect.value : '';
    
    // Validate email
    if (!this.validateEmail(email)) {
      if (feedbackEl) {
        feedbackEl.textContent = texts.feedbackEmailInvalid || 'Email inválido';
        feedbackEl.className = 'budget-feedback error';
      }
      return;
    }
    
    // Show sending state
    if (feedbackEl) {
      feedbackEl.textContent = texts.feedbackSending || 'Enviando presupuesto...';
      feedbackEl.className = 'budget-feedback sending';
    }
    
    // Store the selected store email for use in sendBudget
    this.selectedStoreEmail = storeEmail;
    
    // Call the existing sendBudget function
    this.sendBudget(email).then(() => {
      if (feedbackEl) {
        feedbackEl.textContent = texts.feedbackSuccess || '¡Enviado correctamente!';
        feedbackEl.className = 'budget-feedback success';
      }
      // Clear input after success
      if (emailInput) emailInput.value = '';
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        const modalOverlay = document.querySelector('[data-budget-modal]');
        if (modalOverlay) {
          modalOverlay.style.display = 'none';
          document.body.style.overflow = '';
          this.resetModalSteps(modalOverlay);
        }
      }, 1500);
    }).catch(() => {
      if (feedbackEl) {
        feedbackEl.textContent = texts.feedbackError || 'Error al enviar';
        feedbackEl.className = 'budget-feedback error';
      }
    });
  }

  showEmailFeedback(msg, type) {
    const el = document.querySelector('.budget-email-feedback');
    if (el) {
      el.textContent = msg;
      el.className = `budget-email-feedback ${type}`;
      el.style.display = 'block';
      setTimeout(() => { el.style.display = 'none'; }, 5000);
    }
  }

  async sendBudget(email) {
    const texts = this.config.texts;
    if (!this.config.emailjs || !this.config.emailjs.publicKey) {
      console.error('EmailJS no configurado');
      this.showEmailFeedback(texts.feedbackError || 'Error configuración EmailJS', 'error');
      return;
    }

    if (!this.emailJsInitialized) {
      emailjs.init(this.config.emailjs.publicKey);
      this.emailJsInitialized = true;
    }

    const btn = document.querySelector('[data-action="send-budget-email"]');
    if (btn) {
      btn.disabled = true;
      btn.textContent = '...';
    }
    
    this.showEmailFeedback(texts.feedbackSending || 'Enviando...', 'success');

    try {
      // Calculate Global Monthly Subscription Total using sum of rounded values for consistency
      let grandMonthlyRounded = 0;
      
      // 1. Glasses products (subscription figure, force subscription)
      this.products.forEach(p => {
          if (!p || p.isContactLens || !p.type) return;
          const pTotal = this.getGlassesTotal(p, true);
          grandMonthlyRounded += Math.round(pTotal / this.config.subscriptionMonths);
      });

      // 2. Contact lens products
      if (this.contactLensProducts && this.contactLensProducts.length > 0) {
        this.contactLensProducts.forEach(clProduct => {
          if (!clProduct || !clProduct.model) return;
          let pTotal = clProduct.model.price || 0;
          pTotal += clProduct.personalization || 0;
          if (clProduct.accessory1) pTotal += clProduct.accessory1.price;
          if (clProduct.accessory2) pTotal += clProduct.accessory2.price;
          grandMonthlyRounded += Math.round(pTotal / this.config.subscriptionMonths);
        });
      }

      const totalMonthlyStr = this.formatMoney(grandMonthlyRounded);

      // Get Selected Store Email
      const storeSelect = document.querySelector('[data-store-select]');
      const storeEmail = storeSelect ? storeSelect.value : '';


      // Get observations from Step 1
      const observationsTextarea = document.querySelector('[data-observations]');
      const observations = observationsTextarea ? observationsTextarea.value.trim() : '';

      // Generate unique budget number
      const budgetNumber = this.generateBudgetNumber();

      // Determine reply_to and store_email based on store selection
      const hasStoreEmail = storeEmail && storeEmail.trim() !== '';
      const defaultReplyEmail = this.config.defaultReplyEmail || '';
      
      const templateParams = {
        to_email: email,
        budget_number: budgetNumber, // Unique budget reference number
        budget_summary: this.generateEmailBody(observations, budgetNumber),
        total_monthly: totalMonthlyStr,
        subscription_months: this.config.subscriptionMonths,
        observations: observations, // Also pass as separate param for template flexibility
        message: this.config.texts.emailSubject || "Presupuesto de suscripción Miller & Marc",
        // Reply-to: use store email if available, otherwise use default
        store_email: hasStoreEmail ? storeEmail : defaultReplyEmail
      };
      
      // Only include bcc if store has a valid email
      if (hasStoreEmail) {
        templateParams.bcc_email = storeEmail;
      }

      await emailjs.send(
        this.config.emailjs.serviceId,
        this.config.emailjs.templateId,
        templateParams
      );

      this.showEmailFeedback(texts.feedbackSuccess || '¡Enviado!', 'success');
      const input = document.querySelector('.budget-email-input');
      if (input) input.value = '';

      // Close modal after success
      setTimeout(() => {
        const modalOverlay = document.querySelector('[data-budget-modal]');
        if (modalOverlay) {
            modalOverlay.style.display = 'none';
            document.body.style.overflow = '';
        }
      }, 2000);

    } catch (error) {
      console.error('EmailJS Error:', error);
      this.showEmailFeedback(texts.feedbackError || 'Error al enviar.', 'error');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = this.config.texts.buttonSendBudget || 'Enviar presupuesto';
      }
    }
  }

  initIframeEmailCapture() {
    // Listen for postMessage from iframe
    window.addEventListener('message', (event) => {
      // Security: verify origin if needed
      // if (event.origin !== 'https://calendar.millerandmarc.com') return;
    
      
      // Check if message is from the form embed
      if (event.data && event.data.type === 'formSubmitted') {
        const email = event.data.email;
        
        if (email && this.validateEmail(email)) {
          // Trigger our EmailJS flow
          this.sendBudget(email);
        } else {
          console.warn('⚠️ Invalid email received from iframe:', email);
        }
      }
      
      // Alternative: listen for form submission event
      if (event.data && event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormSubmitted') {
        // HubSpot forms send this event
        const formData = event.data.data;
        const emailField = formData?.find(field => field.name === 'email');
        
        if (emailField && emailField.value) {
          this.sendBudget(emailField.value);
        }
      }
    });
    
  }

  generateEmailBody(observations = '', budgetNumber = '') {
    let html = '<div style="font-family: sans-serif; color: #1f2937; max-width: 600px; margin: 0 auto;">';
    const texts = this.config.texts;
    
    // Add budget number header if provided
    if (budgetNumber) {
      html += `
        <div style="text-align: right; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb;">
          <span style="font-size: 12px; color: #6b7280; text-transform: uppercase;">Nº Presupuesto</span>
          <div style="font-size: 18px; font-weight: 700; color: #1f2937; font-family: monospace;">#${budgetNumber}</div>
        </div>
      `;
    }

    this.products.forEach((p, i) => {
      if (!p || !p.type) return;
      
      const typeName = this.lensTypes.find(t => t.handle === p.type)?.name || p.type;
      const frameName = p.frame ? p.frame.tier : (texts.textModelNotSelected || 'Modelo no seleccionado');
      const framePrice = p.frame ? p.frame.price : 0;

      const subtotal = this.getProductSubtotal(p);
      const pTotal = this.getGlassesTotal(p);
      
      // Logic dependent on mode
      let finalPriceText = '';
      let finalLabelText = '';
      let perMonthText = '';
      let subscriptionComparisonHTML = '';

      if (this.isSubscription) {
          const pMonthly = pTotal / this.config.subscriptionMonths;
          finalPriceText = this.formatMoney(Math.round(pMonthly));
          finalLabelText = texts.labelSubscriptionFee || 'Cuota suscripción';
          perMonthText = texts.textPerMonth || '/ mes';
      } else {
          finalPriceText = this.formatMoney(pTotal);
          finalLabelText = texts.labelTotal || 'Total';
          perMonthText = '';
          
          // Calculate subscription comparison for Purchase Mode (force subscription)
          const monthlySub = this.getGlassesTotal(p, true) / this.config.subscriptionMonths;
          
          subscriptionComparisonHTML = `
            <div style="margin-top: 16px; padding-top: 16px; border-top: 1px dashed #d1d5db; text-align: center;">
                <span style="font-size: 13px; color: #4b5563; font-weight: 500;">${texts.textSubscribeCallout || 'Suscríbete'}</span>
                <span style="font-size: 16px; font-weight: 700; color: #1f2937; margin-left: 6px;">
                    ${this.formatMoney(Math.round(monthlySub))} <span style="font-size: 12px; font-weight: 400; color: #6b7280;">${texts.textPerMonth || '/ mes'}</span>
                </span>
            </div>
          `;
      }
      
      const row = (label, price) => `
        <tr>
          <td style="padding: 8px 0; color: #6b7280; font-size: 14px; border-bottom: 1px solid #f3f4f6;">${label}</td>
          <td style="padding: 8px 0; text-align: right; color: #1f2937; font-weight: 500; font-size: 14px; border-bottom: 1px solid #f3f4f6;">${this.formatMoney(price)}</td>
        </tr>
      `;

      let detailsRows = '';
      if (p.index) detailsRows += row(`${texts.labelIndex || 'Índice'} ${p.index.value}`, p.index.price);
      if (p.treatment1) detailsRows += row(p.treatment1.name, p.treatment1.price * 2);
      if (p.treatment2) detailsRows += row(p.treatment2.name, p.treatment2.price * 2);
      if (p.treatment3) detailsRows += row(p.treatment3.name, p.treatment3.price * 2);
      if (p.personalization > 0) detailsRows += row(texts.labelPersonalization || 'Personalización', p.personalization);
      if (p.accessory1) detailsRows += row(p.accessory1.name, p.accessory1.price);
      if (p.accessory2) detailsRows += row(p.accessory2.name, p.accessory2.price);
      
      if (this.isSubscription) {
        detailsRows += row(this.getAdditionalLabel(), this.getAdditionalAmount(subtotal));
      }

      // Get product image for email (ensure https: protocol for email clients)
      let productImage = p.frame?.image || this.getProductImage(p.type);
      if (productImage && productImage.startsWith('//')) {
        productImage = 'https:' + productImage;
      }
      const imageHTML = productImage 
        ? `<img src="${productImage}" alt="${frameName}" width="120" height="120" style="display: block; margin: 0 auto; border-radius: 8px; object-fit: cover;">`
        : `<span style="font-size: 48px; color: #cbd5e1; line-height: 1;">👓</span>`;

      html += `
        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border: 1px solid #e5e7eb; border-radius: 12px; background-color: #ffffff; margin-bottom: 24px; overflow: hidden;">
          <tr>
            <td align="center" style="background-color: #f9fafb; padding: 30px;">
               ${imageHTML}
            </td>
          </tr>
          <tr>
            <td style="padding: 24px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="font-size: 18px; font-weight: 600; color: #1f2937; padding-bottom: 4px;">${frameName}</td>
                  <td align="right" style="font-size: 16px; font-weight: 600; color: #1f2937; padding-bottom: 4px;">${this.formatMoney(framePrice)}</td>
                </tr>
                <tr>
                  <td colspan="2" style="font-size: 14px; color: #6b7280; padding-bottom: 20px;">${typeName}</td>
                </tr>
              </table>
              
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                ${detailsRows}
              </table>
              
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top: 20px; border-top: 1px solid #e5e7eb;">
                <tr>
                  <td align="center" style="padding-top: 16px;">
                    <div style="font-size: 12px; text-transform: uppercase; color: #6b7280; margin-bottom: 4px;">${finalLabelText}</div>
                    <div style="font-size: 24px; font-weight: 700; color: #1f2937;">
                      ${finalPriceText} <span style="font-size: 16px; font-weight: 400; color: #6b7280;">${perMonthText}</span>
                    </div>
                    ${subscriptionComparisonHTML}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      `;
    });

    // 2. Add Contact Lens Products to Email
    if (this.contactLensProducts && this.contactLensProducts.length > 0) {
      this.contactLensProducts.forEach(clProduct => {
        if (!clProduct || !clProduct.model) return;

        const typeName = this.contactLensTypes.find(t => t.handle === clProduct.type)?.name || clProduct.type || 'Lentillas';
        const modelName = clProduct.model.name || clProduct.model.productTitle || 'Modelo';
        const modelPrice = clProduct.model.price || 0;

        let pTotal = modelPrice;
        pTotal += clProduct.personalization || 0;
        if (clProduct.accessory1) pTotal += clProduct.accessory1.price;
        if (clProduct.accessory2) pTotal += clProduct.accessory2.price;

        // Logic dependent on mode
        let finalPriceText = '';
        let finalLabelText = '';
        let perMonthText = '';
        let subscriptionComparisonHTML = '';

        if (this.isSubscription) {
          const pMonthly = pTotal / this.config.subscriptionMonths;
          finalPriceText = this.formatMoney(Math.round(pMonthly));
          finalLabelText = texts.labelSubscriptionFee || 'Cuota suscripción';
          perMonthText = texts.textPerMonth || '/ mes';
        } else {
          finalPriceText = this.formatMoney(pTotal);
          finalLabelText = texts.labelTotal || 'Total';
          perMonthText = '';
          
          // Calculate subscription comparison for Purchase Mode (contact lenses don't include warranty)
          const monthlySub = pTotal / this.config.subscriptionMonths;
          
          subscriptionComparisonHTML = `
            <div style="margin-top: 16px; padding-top: 16px; border-top: 1px dashed #d1d5db; text-align: center;">
                <span style="font-size: 13px; color: #4b5563; font-weight: 500;">${texts.textSubscribeCallout || 'Suscríbete'}</span>
                <span style="font-size: 16px; font-weight: 700; color: #1f2937; margin-left: 6px;">
                    ${this.formatMoney(Math.round(monthlySub))} <span style="font-size: 12px; font-weight: 400; color: #6b7280;">${texts.textPerMonth || '/ mes'}</span>
                </span>
            </div>
          `;
        }

        const row = (label, price) => `
          <tr>
            <td style="padding: 8px 0; color: #6b7280; font-size: 14px; border-bottom: 1px solid #f3f4f6;">${label}</td>
            <td style="padding: 8px 0; text-align: right; color: #1f2937; font-weight: 500; font-size: 14px; border-bottom: 1px solid #f3f4f6;">${this.formatMoney(price)}</td>
          </tr>
        `;

        let detailsRows = '';
        if (clProduct.personalization > 0) detailsRows += row(texts.labelPersonalization || 'Otros', clProduct.personalization);
        if (clProduct.accessory1) detailsRows += row(clProduct.accessory1.name, clProduct.accessory1.price);
        if (clProduct.accessory2) detailsRows += row(clProduct.accessory2.name, clProduct.accessory2.price);

        // Get contact lens image for email (ensure https: protocol for email clients)
        let clImage = this.getProductImage(null, true);
        if (clImage && clImage.startsWith('//')) {
          clImage = 'https:' + clImage;
        }
        const clImageHTML = clImage 
          ? `<img src="${clImage}" alt="${modelName}" width="120" height="120" style="display: block; margin: 0 auto; border-radius: 8px; object-fit: cover;">`
          : `<span style="font-size: 48px; color: #cbd5e1; line-height: 1;">👁️</span>`;

        html += `
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border: 1px solid #e5e7eb; border-radius: 12px; background-color: #ffffff; margin-bottom: 24px; overflow: hidden;">
            <tr>
              <td align="center" style="background-color: #f9fafb; padding: 30px;">
                 ${clImageHTML}
              </td>
            </tr>
            <tr>
              <td style="padding: 24px;">
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td style="font-size: 18px; font-weight: 600; color: #1f2937; padding-bottom: 4px;">${modelName}</td>
                    <td align="right" style="font-size: 16px; font-weight: 600; color: #1f2937; padding-bottom: 4px;">${this.formatMoney(modelPrice)}</td>
                  </tr>
                  <tr>
                    <td colspan="2" style="font-size: 14px; color: #6b7280; padding-bottom: 20px;">${typeName}</td>
                  </tr>
                </table>
                
                ${detailsRows ? `
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  ${detailsRows}
                </table>
                ` : ''}
                
                <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top: 20px; border-top: 1px solid #e5e7eb;">
                  <tr>
                    <td align="center" style="padding-top: 16px;">
                      <div style="font-size: 12px; text-transform: uppercase; color: #6b7280; margin-bottom: 4px;">${finalLabelText}</div>
                      <div style="font-size: 24px; font-weight: 700; color: #1f2937;">
                        ${finalPriceText} <span style="font-size: 16px; font-weight: 400; color: #6b7280;">${perMonthText}</span>
                      </div>
                      ${subscriptionComparisonHTML}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        `;
      });
    }

    // Add observations section at the end if provided
    if (observations) {
      html += `
        <div style="background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-top: 24px;">
          <div style="font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px;">${texts.step1ObservationsLabel || 'Observaciones'}</div>
          <div style="font-size: 14px; color: #4b5563; white-space: pre-wrap;">${observations}</div>
        </div>
      `;
    }
    
    // Calculate grand total for footer
    let grandTotal = 0;
    this.products.forEach(p => {
      if (!p || !p.type) return;
      grandTotal += this.getGlassesTotal(p);
    });
    
    if (this.contactLensProducts && this.contactLensProducts.length > 0) {
      this.contactLensProducts.forEach(clProduct => {
        if (!clProduct || !clProduct.model) return;
        let pTotal = clProduct.model.price || 0;
        pTotal += clProduct.personalization || 0;
        if (clProduct.accessory1) pTotal += clProduct.accessory1.price;
        if (clProduct.accessory2) pTotal += clProduct.accessory2.price;
        grandTotal += pTotal;
      });
    }
    
    // Footer section with total and CTA
    const faqUrl = texts.emailSubscriptionFaqUrl || '/pages/preguntas-frecuentes';
    const faqFullUrl = faqUrl.startsWith('http') ? faqUrl : `${this.config.shopUrl}${faqUrl}`;
    
    if (this.isSubscription) {
      // SUBSCRIPTION MODE: Show monthly total - left aligned, no background
      const monthlyTotal = Math.round(grandTotal / this.config.subscriptionMonths);
      const planText = (texts.emailSubscriptionPlanText || '(Plan de suscripción de {months} meses)').replace('{months}', this.config.subscriptionMonths);
      html += `
        <div style="margin-top: 32px; text-align: left;">
          <div style="font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 4px;">${texts.emailMonthlyTotalLabel || 'Total Mensual:'} ${this.formatMoney(monthlyTotal)}</div>
          <div style="font-size: 14px; color: #1f2937; margin-bottom: 24px;">${planText}</div>
          <div style="font-size: 14px; color: #1f2937; margin-bottom: 16px;">
            ${(() => {
              const faqText = texts.emailSubscriptionFaqText || 'Si necesitas cualquier aclaración contáctanos o echa un vistazo a las {link}.';
              const faqLink = `<a href="${faqFullUrl}" style="color: #1f2937; text-decoration: underline;">${texts.emailFaqLinkText || 'preguntas frecuentes de suscripción'}</a>`;
              if (faqText.includes('{link}')) {
                return faqText.replace('{link}', faqLink);
              } else {
                return faqText + ' ' + faqLink + '.';
              }
            })()}
          </div>
          <div style="font-size: 14px; color: #1f2937; margin-bottom: 24px;">
            ${texts.emailSubscriptionReminderText || 'Si todavía no has activado tu suscripción, te recordamos que es necesario realizar la gestión físicamente en tienda.'}
          </div>
          <div style="font-size: 14px; color: #1f2937; margin-bottom: 16px;">${texts.emailGreeting || '¡Un saludo!'}</div>
          <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${texts.emailSignature || 'Equipo Miller & Marc'}</div>
        </div>
      `;
    } else {
      // PURCHASE MODE: Show total and buy button - left aligned, no background
      const cartPermalink = this.generateCartPermalink();
      
      // Check if there are glasses products (they don't have variantId)
      const hasGlassesProducts = this.products && this.products.some(p => p && p.type);
      // Only show buy button if there are NO glasses (only contact lenses with variantId)
      const showBuyButton = !hasGlassesProducts && cartPermalink;
      
      html += `
        <div style="margin-top: 32px; text-align: left;">
          <div style="font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 16px;">${texts.emailPurchaseTotalLabel || 'Total:'} ${this.formatMoney(grandTotal)}</div>
          <div style="font-size: 14px; color: #1f2937; margin-bottom: 24px;">
            ${texts.emailPurchaseCtaText || 'Puedes realizar tu encargo en tienda o en nuestra web añadiendo los productos al carrito.'}
          </div>
          ${showBuyButton ? `
          <div style="margin-bottom: 32px; text-align: center;">
            <a href="${cartPermalink}" style="display: inline-block; background-color: #000000; color: #ffffff; padding: 14px 48px; border-radius: 50px; text-decoration: none; font-weight: 600; font-size: 14px;">${texts.emailPurchaseButtonText || 'Comprar'}</a>
          </div>
          ` : ''}
          <div style="font-size: 14px; color: #1f2937; margin-bottom: 24px;">
            ${texts.emailClosingText || 'Si tienes cualquier duda o necesitas cualquier ajuste, estamos encantados de ayudarte.'}
          </div>
          <div style="font-size: 14px; color: #1f2937; margin-bottom: 16px;">${texts.emailGreeting || '¡Un saludo!'}</div>
          <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${texts.emailSignature || 'Equipo Miller & Marc'}</div>
        </div>
      `;
    }
    
    html += '</div>';
    return html;
  }

  renderSummary() {
    const grid = document.querySelector('[data-summary-grid]');
    if (!grid) return;
    grid.innerHTML = '';

    let grandMonthlyRounded = 0; // Sum of individually rounded monthly values

    const renderRow = (label, price) => `
      <div class="summary-detail">
        <span class="summary-detail-label">${label}</span>
        <span class="summary-detail-value">${this.formatMoney(price)}</span>
      </div>`;

    // 1. Render Glasses Products
    this.products.forEach(p => {
      // Skip contact lens products (handled separately)
      if (!p || p.isContactLens || !p.type) return;

      const card = document.createElement('div');
      card.className = 'summary-card';
      
      const typeName = this.lensTypes.find(t => t.handle === p.type)?.name || p.type;
      const frameName = p.frame ? p.frame.tier : (this.config.texts.textModelNotSelected || 'Modelo no seleccionado');
      
      const subtotal = this.getProductSubtotal(p);
      const pTotal = this.getGlassesTotal(p);

      const pMonthly = pTotal / this.config.subscriptionMonths;
      const pMonthlyRounded = Math.round(pMonthly);
      grandMonthlyRounded += pMonthlyRounded;

      let detailsHTML = '';
      if (p.index) detailsHTML += renderRow(`${this.config.texts.labelIndex || 'Índice'} ${p.index.value}`, p.index.price);
      if (p.treatment1) detailsHTML += renderRow(p.treatment1.name, p.treatment1.price * 2);
      if (p.treatment2) detailsHTML += renderRow(p.treatment2.name, p.treatment2.price * 2);
      if (p.treatment3) detailsHTML += renderRow(p.treatment3.name, p.treatment3.price * 2);
      if (p.personalization > 0) detailsHTML += renderRow(this.config.texts.labelPersonalization || 'Personalización', p.personalization);
      if (p.accessory1) detailsHTML += renderRow(p.accessory1.name, p.accessory1.price);
      if (p.accessory2) detailsHTML += renderRow(p.accessory2.name, p.accessory2.price);

      // Additional row: +Beneficios (legacy) or -Descuento
      if (this.isSubscription) {
        detailsHTML += renderRow(this.getAdditionalLabel(), this.getAdditionalAmount(subtotal));
      }

      // Footer Logic
      const valueText = this.isSubscription
          ? `${this.formatMoney(pMonthlyRounded)} ${this.config.texts.textPerMonth || '/ mes'}`
          : this.formatMoney(pTotal);

      const labelText = this.isSubscription
          ? (this.config.additionalRowMode === 'benefits'
              ? (this.config.texts.labelSubscriptionTotal || 'Total garantía incl.')
              : (this.config.texts.discountTotalLabel || 'Total'))
          : (this.config.texts.labelTotal || 'Total');

      const footerHTML = `
          <div class="product-total">
            <span class="product-total-label">${labelText}</span>
            <span class="product-total-value">${valueText}</span>
          </div>`;

      // Get product image (from frame or default)
      const productImage = p.frame?.image || this.getProductImage(p.type);
      
      card.innerHTML = `
        <div class="summary-card-image${!productImage ? ' placeholder' : ''}">
          ${productImage 
            ? `<img src="${productImage}" alt="${frameName}" loading="lazy">`
            : `<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <circle cx="6" cy="12" r="4"></circle>
                <circle cx="18" cy="12" r="4"></circle>
                <line x1="10" y1="12" x2="14" y2="12"></line>
               </svg>`
          }
        </div>
        <div class="summary-card-body">
          <div class="summary-header">
            <h3 class="summary-card-title">${frameName}</h3>
            <span class="summary-price">${p.frame ? this.formatMoney(p.frame.price) : ''}</span>
          </div>
          <div class="summary-subtitle">${typeName}</div>
          
          <div class="summary-card-details">
            ${detailsHTML}
          </div>
          
          ${footerHTML}
        </div>
      `;
      grid.appendChild(card);
    });

    // 2. Render Contact Lens Products
    if (this.contactLensProducts && this.contactLensProducts.length > 0) {
      this.contactLensProducts.forEach(clProduct => {
        if (!clProduct || !clProduct.model) return;

        const card = document.createElement('div');
        card.className = 'summary-card summary-card--contact-lens';
        
        const typeName = this.contactLensTypes.find(t => t.handle === clProduct.type)?.name || clProduct.type || 'Lentillas';
        const modelName = clProduct.model.name || clProduct.model.productTitle || 'Modelo';
        const modelPrice = clProduct.model.price || 0;
        
        let pTotal = modelPrice;
        pTotal += clProduct.personalization || 0;
        if (clProduct.accessory1) pTotal += clProduct.accessory1.price;
        if (clProduct.accessory2) pTotal += clProduct.accessory2.price;

        const pMonthly = pTotal / this.config.subscriptionMonths;
        const pMonthlyRounded = Math.round(pMonthly);
        grandMonthlyRounded += pMonthlyRounded;

        let detailsHTML = '';
        if (clProduct.personalization > 0) detailsHTML += renderRow(this.config.texts.labelPersonalization || 'Otros', clProduct.personalization);
        if (clProduct.accessory1) detailsHTML += renderRow(clProduct.accessory1.name, clProduct.accessory1.price);
        if (clProduct.accessory2) detailsHTML += renderRow(clProduct.accessory2.name, clProduct.accessory2.price);

        // Footer Logic
        const valueText = this.isSubscription 
            ? `${this.formatMoney(pMonthlyRounded)} ${this.config.texts.textPerMonth || '/ mes'}`
            : this.formatMoney(pTotal);

        const labelText = this.isSubscription
            ? (this.config.additionalRowMode === 'benefits'
                ? (this.config.texts.labelSubscriptionTotal || 'Total garantía incl.')
                : (this.config.texts.discountTotalLabel || 'Total'))
            : (this.config.texts.labelTotal || 'Total');

        const footerHTML = `
            <div class="product-total">
              <span class="product-total-label">${labelText}</span>
              <span class="product-total-value">${valueText}</span>
            </div>`;

        // Get contact lens image
        const clImage = this.getProductImage(null, true);
        
        card.innerHTML = `
          <div class="summary-card-image${!clImage ? ' placeholder' : ''}">
            ${clImage 
              ? `<img src="${clImage}" alt="${modelName}" loading="lazy">`
              : `<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                  <circle cx="12" cy="12" r="9"></circle>
                  <circle cx="12" cy="12" r="5"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>`
            }
          </div>
          <div class="summary-card-body">
            <div class="summary-header">
              <h3 class="summary-card-title">${modelName}</h3>
              <span class="summary-price">${this.formatMoney(modelPrice)}</span>
            </div>
            <div class="summary-subtitle">${typeName}</div>
            
            <div class="summary-card-details">
              ${detailsHTML}
            </div>
            
            ${footerHTML}
          </div>
        `;
        grid.appendChild(card);
      });
    }
    
    // Update Floating Total Step 3 - use sum of rounded values for consistency
    const totalMonthlyStep3 = document.querySelector('[data-total-monthly-step3]');
    if (totalMonthlyStep3) {
      totalMonthlyStep3.textContent = this.formatMoney(grandMonthlyRounded);
    }
  }

  /* ==========================================================================
     NAVIGATION
     ========================================================================== */

  initNavigation() {
    // Next: Step 1 -> 2
    const btnNext1 = document.querySelector('[data-action="next"]');
    if (btnNext1) {
      btnNext1.addEventListener('click', () => {
        const glassesSelections = this.collectMatrixSelections();
        const contactLensSelections = this.collectContactLensSelections();
        this.generateSimulatorColumns(glassesSelections, contactLensSelections);
        this.showStep(2);
      });
    }

    // Next: Step 2 -> 3
    const btnNext2 = document.querySelector('[data-action="next-step-3"]');
    if (btnNext2) {
      btnNext2.addEventListener('click', () => {
        this.renderSummary();
        this.showStep(3);
      });
    }

    // Global Back Button
    const btnBack = document.querySelector('[data-global-back]');
    if (btnBack) {
      btnBack.addEventListener('click', () => {
        if (this.currentStep > 1) {
          this.showStep(this.currentStep - 1);
        }
      });
    }
  }

  initDraggableSummary() {
    const card = document.querySelector('[data-floating-card]');
    if (!card) return;
    
    // State for drag
    let currentX = 0;
    let currentY = 0;
    let initialX = 0;
    let initialY = 0;
    let xOffset = 0;
    let yOffset = 0;
    let isDragging = false;

    const dragStart = (e) => {
      // Delegate: Only start if clicking header
      // This allows innerHTML to change without breaking listeners
      if (!e.target.closest('.summary-card-header')) return;
      
      if (e.type === "touchstart") {
        initialX = e.touches[0].clientX - xOffset;
        initialY = e.touches[0].clientY - yOffset;
      } else {
        initialX = e.clientX - xOffset;
        initialY = e.clientY - yOffset;
      }

      if (card.contains(e.target)) {
        isDragging = true;
      }
    };

    const dragEnd = (e) => {
      initialX = currentX;
      initialY = currentY;
      isDragging = false;
    };

    const drag = (e) => {
      if (isDragging) {
        e.preventDefault(); // Prevent scroll on touch
      
        if (e.type === "touchmove") {
          currentX = e.touches[0].clientX - initialX;
          currentY = e.touches[0].clientY - initialY;
        } else {
          currentX = e.clientX - initialX;
          currentY = e.clientY - initialY;
        }

        xOffset = currentX;
        yOffset = currentY;

        card.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
      }
    };

    // Attach to card (wrapper) which persists
    // Using passive: false for touchmove to allow preventDefault
    card.addEventListener("touchstart", dragStart, {passive: false});
    document.addEventListener("touchend", dragEnd, false);
    document.addEventListener("touchmove", drag, {passive: false});

    card.addEventListener("mousedown", dragStart, false);
    document.addEventListener("mouseup", dragEnd, false);
    document.addEventListener("mousemove", drag, false);
  }

  showStep(step) {
    this.currentStep = step;
    
    // Show/Hide Steps
    document.querySelectorAll('.calculator-step').forEach(el => {
      el.style.display = el.dataset.step == step ? 'block' : 'none';
    });
    
    // Update Global Header
    this.updateGlobalHeader(step);
    
    // Show/Hide Floating Card (now includes Step 1)
    const floatCard = document.querySelector('[data-floating-card]');
    if (floatCard) {
        const shouldShow = step >= 1; // Changed from step > 1 to include Step 1
        floatCard.style.display = shouldShow ? 'block' : 'none';
        
        if (shouldShow) {
            this.updatePricesUI();
            // Init drag once
            if (!this.draggableInitialized) {
                this.initDraggableSummary();
                this.draggableInitialized = true;
            }
        }
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  updateGlobalHeader(step) {
    // Update Counter
    const counter = document.querySelector('[data-step-counter]');
    if (counter) {
      counter.textContent = `${step} de 3`;
    }
    
    // Update Progress Bar
    const progressBar = document.querySelector('[data-progress-bar-fill]');
    if (progressBar) {
        const percent = (step / 3) * 100;
        progressBar.style.width = `${percent}%`;
    }

    // Toggle Back Button
    const backBtn = document.querySelector('[data-global-back]');
    if (backBtn) {
      backBtn.style.visibility = step > 1 ? 'visible' : 'hidden';
    }
  }

  /* ==========================================================================
     UTILS
     ========================================================================== */

  formatMoney(cents) {
    const euros = cents / 100;
    // If no decimals, don't show them
    const hasDecimals = euros % 1 !== 0;
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0
    }).format(euros);
  }

  generateBudgetNumber() {
    // Generate unique 6-character alphanumeric budget number based on timestamp
    return Date.now().toString(36).slice(-6).toUpperCase();
  }

  generateCartPermalink() {
    // Generate Shopify cart permalink with all selected products
    // Format: https://shop.myshopify.com/cart/variant_id:quantity,variant_id:quantity?storefront=true
    const items = [];
    
    // Collect variant IDs from contact lens products
    if (this.contactLensProducts && this.contactLensProducts.length > 0) {
      this.contactLensProducts.forEach(clProduct => {
        if (clProduct.model && clProduct.model.variantId) {
          items.push(`${clProduct.model.variantId}:1`);
        }
        // Add accessories
        if (clProduct.accessory1 && clProduct.accessory1.variantId) {
          items.push(`${clProduct.accessory1.variantId}:1`);
        }
        if (clProduct.accessory2 && clProduct.accessory2.variantId) {
          items.push(`${clProduct.accessory2.variantId}:1`);
        }
      });
    }
    
    // Collect variant IDs from glasses accessories
    if (this.products && this.products.length > 0) {
      this.products.forEach(product => {
        if (product.accessory1 && product.accessory1.variantId) {
          items.push(`${product.accessory1.variantId}:1`);
        }
        if (product.accessory2 && product.accessory2.variantId) {
          items.push(`${product.accessory2.variantId}:1`);
        }
      });
    }
    
    if (items.length === 0) {
      return null;
    }
    
    const shopUrl = this.config.shopUrl || '';
    return `${shopUrl}/cart/${items.join(',')}?storefront=true`;
  }
}

// Init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SubscriptionCalculator());
} else {
  new SubscriptionCalculator();
}
